import * as currency from 'currency.js';
import { CURRENCIES as isoCurrencies } from '../resources/iso_currencies';
const winstonKey = 'shared.helpers.currencyWrapper.abacusCurrency';
const defaultCurrencyCode = 'USD';
export const isoCurrenciesByCode = {};
isoCurrencies.forEach(function (isoCurrency) {
    if (isoCurrency.alphabetic_code) {
        isoCurrenciesByCode[isoCurrency.alphabetic_code] = isoCurrency;
    }
});
export class AbacusCurrency {
    constructor(amount, currencyCode, isAmountMajorUnits) {
        this.getUnitValue = () => this.currencyObject.value;
        this.getMinorUnitValue = () => this.currencyObject.intValue;
        this.addMinorAmount = (amount, currencyCode) => this.addMajorAmount(this.getCurrencyObjectForAggregating(amount, currencyCode));
        this.subtractMinorAmount = (amount, currencyCode) => this.subtractMajorAmount(this.getCurrencyObjectForAggregating(amount, currencyCode));
        if (!currencyCode) {
            currencyCode = defaultCurrencyCode;
        }
        if (!isoCurrenciesByCode[currencyCode]) {
            throw new Error('expected valid currency code');
        }
        this.currency = isoCurrenciesByCode[currencyCode];
        this.currencyObject = currency(amount, { fromCents: !isAmountMajorUnits, precision: this.currency.exponent });
    }
    getCurrencyObjectForAggregating(amount, currencyCode, isAmountMajorUnits) {
        const isUsingDefaultUSD = !currencyCode && this.currency.alphabetic_code === defaultCurrencyCode;
        // throw if the base currency code is not USD and currencyCode is either undefined, or provided currency code does not match the original currency code
        if (!isUsingDefaultUSD && currencyCode !== this.currency.alphabetic_code) {
            throw new Error(`${winstonKey}.addMinorAmount.CURRENCY_CODE_MUST_MATCH`);
        }
        return currency(amount, { fromCents: !isAmountMajorUnits, precision: this.currency.exponent });
    }
    addMajorAmount(amount) {
        const currencyToAdd = this.getCurrencyObjectForAggregating(amount, this.currency.alphabetic_code, true);
        const newAmountObject = this.currencyObject.add(currencyToAdd);
        return new AbacusCurrency(newAmountObject.value, this.currency.alphabetic_code, true);
    }
    subtractMajorAmount(amount) {
        const currencyToSubtract = this.getCurrencyObjectForAggregating(amount, this.currency.alphabetic_code, true);
        const newAmountObject = this.currencyObject.subtract(currencyToSubtract);
        return new AbacusCurrency(newAmountObject.value, this.currency.alphabetic_code, true);
    }
    divide(divisor) {
        const newAmountObject = this.currencyObject.divide(divisor);
        return new AbacusCurrency(newAmountObject.value, this.currency.alphabetic_code, true);
    }
    multiply(multiple) {
        const newAmountObject = this.currencyObject.multiply(multiple);
        return new AbacusCurrency(newAmountObject.value, this.currency.alphabetic_code, true);
    }
    /**
     * Converts the instance currency object given the exchange rate and provided currency code.
     * Use caution with logic using inverted rates (e.g. from param currency code -> instance currency object)
     * @see convertAmountToTargetCurrency
     * @param exchangeRate
     * @param currencyCode
     * @returns
     */
    convert(exchangeRate, currencyCode) {
        const newAmountObject = this.currencyObject.multiply(exchangeRate);
        return new AbacusCurrency(newAmountObject.value, currencyCode, true);
    }
    split(numberOfSplits) {
        const splits = this.currencyObject.distribute(numberOfSplits);
        return splits.map((split) => new AbacusCurrency(split.value, this.currency.alphabetic_code, true));
    }
    toFormattedString(pruneZeroDecimals = false) {
        const formattedString = this.currencyObject.format();
        if (pruneZeroDecimals) {
            return formattedString.replace(/\.0+$/, '');
        }
        return formattedString;
    }
    // this will return the currency value in major units with decimal padding (no currency marker)
    getUnitStringValueWithDecimals() {
        return this.currencyObject.toString();
    }
}
